<template>
  <div class="organization-modify-page">

    <!-- 横幅页头 -->
    <div class="page-banner">
      <div class="page-banner-icon"><i class="el-icon-office-building"></i></div>
      <div class="page-banner-text">
        <h2 class="page-title">企业信息维护</h2>
        <p class="page-desc">请确保企业基础信息、资质附件与联系人信息准确完整</p>
      </div>
      <div class="page-banner-status" v-if="msgCode == 200">
        <span :class="['status-badge', 'status-' + dict.auditStatus]">
          <i :class="auditStatusIcon"></i> {{ getStatusText(dict) }}
        </span>
      </div>
    </div>

    <!-- 审核联系提示 -->
    <div class="audit-alert" v-if="shouldShowAuditContactTip">
      <i class="el-icon-phone-outline"></i>
      请联系平台客服完成审核，联系电话：<strong>400-963-5558</strong>
    </div>

    <!-- 主内容区 -->
    <div class="info-layout">

      <!-- 左侧：基本信息 + 营业执照 -->
      <div class="info-left">

        <!-- 基本信息卡片 -->
        <div class="info-card">
          <div class="card-header">
            <span class="card-accent"></span>
            <i class="el-icon-s-order card-icon"></i>
            <span class="card-title">基本信息</span>
          </div>
          <div class="card-body">
            <el-form :model="formData" :rules="rules" ref="organizationForm" label-width="130px">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="单位名称" prop="unitName" required>
                    <el-input v-model="formData.unitName" placeholder="请输入单位名称" :disabled="!isEditMode"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="统一社会信用代码" prop="creditCode">
                    <el-input v-model="formData.creditCode" placeholder="请输入18位信用代码" :disabled="!isEditMode"></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="法定代表人" prop="legalRepresentative" required>
                    <el-input v-model="formData.legalRepresentative" placeholder="请输入法定代表人" :disabled="!isEditMode"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="法人电话" prop="legalPhone" required>
                    <el-input v-model="formData.legalPhone" placeholder="请输入11位手机号" :disabled="!isEditMode"></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="法人身份证号" prop="legalIdNo" required>
                    <el-input v-model="formData.legalIdNo" placeholder="请输入18位身份证号" :disabled="!isEditMode" maxlength="18"></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="24">
                  <el-form-item label="单位所在地" required>
                    <div class="location-row">
                      <el-input v-if="!isEditMode" :value="regionText" readonly class="region-cascader"></el-input>
                      <el-cascader v-else ref="regionCascader" v-model="formData.region" placeholder="请选择省市区"
                        :props="regionCascaderProps" @change="handleRegionChange" class="region-cascader" />
                      <el-input v-model="formData.detailedAddress" placeholder="请输入详细地址" class="location-input"
                        :readonly="!isEditMode"></el-input>
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </div>
        </div>

        <!-- 营业执照卡片 -->
        <div class="info-card">
          <div class="card-header">
            <span class="card-accent"></span>
            <i class="el-icon-picture-outline card-icon"></i>
            <span class="card-title">营业执照扫描件</span>
            <span class="card-subtitle">三证合一原件彩色扫描件</span>
          </div>
          <div class="card-body">
            <!-- 已上传 -->
            <div v-if="formData.businessLicense" class="license-preview-box">
              <div class="license-img-wrap">
                <img :src="formData.businessLicense" alt="营业执照" />
                <div class="license-img-overlay">
                  <el-button type="text" icon="el-icon-zoom-in" @click="handlePreviewBusinessLicense">查看大图</el-button>
                  <el-button v-if="isEditMode" type="text" icon="el-icon-delete" @click="removeBusinessLicense">删除</el-button>
                </div>
              </div>
              <div class="license-uploaded-tip"><i class="el-icon-circle-check"></i> 营业执照已上传</div>
            </div>
            <!-- 上传框 -->
            <el-upload v-else-if="isEditMode" class="license-uploader" drag action="#"
              :auto-upload="false" :on-change="handleFileChange" accept=".jpg,.jpeg,.png" :disabled="uploading">
              <div class="license-upload-inner">
                <i class="el-icon-upload license-upload-icon"></i>
                <p class="license-upload-text">点击或拖拽文件到此区域上传</p>
                <p class="license-upload-hint">支持 jpg / jpeg / png，文件不超过 10MB</p>
              </div>
            </el-upload>
            <!-- 查看模式下无图 -->
            <div v-else class="license-empty">
              <i class="el-icon-picture-outline"></i>
              <p>暂未上传营业执照</p>
            </div>
          </div>
        </div>

      </div>

      <!-- 右侧：企业联系人 -->
      <div class="info-right">
        <div class="info-card">
          <div class="card-header">
            <span class="card-accent"></span>
            <i class="el-icon-s-custom card-icon"></i>
            <span class="card-title">企业联系人</span>
          </div>
          <div class="card-body">
            <el-form :model="formData" :rules="rules" ref="contactForm" label-width="110px">
              <el-form-item label="申报责任人" prop="declarant" required>
                <el-input v-model="formData.declarant" placeholder="请输入信息申报责任人" :disabled="!isEditMode"></el-input>
              </el-form-item>
              <el-form-item label="申报人电话" prop="declarantPhone" required>
                <el-input v-model="formData.declarantPhone" placeholder="请输入11位手机号" :disabled="!isEditMode"></el-input>
              </el-form-item>
              <el-form-item label="电子邮箱" prop="email" required>
                <el-input v-model="formData.email" placeholder="请输入电子邮箱" :disabled="!isEditMode"></el-input>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>

    </div>

    <!-- 底部操作栏 -->
    <div class="footer-bar" v-if="msgCode !== 200 || isEditMode || Number(dict.auditStatus) === 1">
      <div class="footer-bar-inner">
        <template v-if="msgCode !== 200">
          <el-button type="primary" size="medium" icon="el-icon-check" @click="handleSave">提交申请</el-button>
        </template>
        <template v-else-if="isEditMode">
          <el-button size="medium" icon="el-icon-close" @click="handleCancel">取消</el-button>
          <el-button type="primary" size="medium" icon="el-icon-check" @click="handleSave">保存修改</el-button>
        </template>
        <template v-else-if="Number(dict.auditStatus) === 1">
          <el-button type="primary" size="medium" icon="el-icon-edit" @click="handleEdit">编辑信息</el-button>
        </template>
      </div>
    </div>

    <el-dialog title="营业执照预览" :visible.sync="businessLicensePreviewVisible" width="70%" :append-to-body="true">
      <div style="text-align:center">
        <img :src="formData.businessLicense" alt="营业执照大图" style="max-width:100%;max-height:70vh;object-fit:contain;" />
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { addOrganization, getOrganizationInfo, updateOrganization } from '@/api/modules/user'
import RegionUtils from '@/utils/regionUtils'
import { doFileUpload } from '@/utils/upload/fileUploadHelper'
import { getFileList } from '@/api/modules/base'
import { checkCreditCodeFormat } from '@/utils/validate'

export default {
  name: 'OrganizationModify',
  data() {
    return {
      dict:{},
      msgCode: '9230',
      isEditMode: false, // 是否为编辑模式，默认为查看模式
      fileGuid: '',
      uploadedFileId: '', // 上传成功后的文件ID
      uploading: false, // 上传中状态
      businessLicensePreviewVisible: false,
      cascaderKey: 0, // 用于强制刷新级联选择器
      formData: {
        id: '',
        // 基本信息
        unitName: '',
        creditCode: '',
        legalRepresentative: '',
        legalPhone: '',
        legalIdNo: '',
        establishmentDate: '',
        // 地区相关字段
        region: [], // 地区选择数组
        province: '', // 省名称
        provinceCode: '', // 省编码
        city: '', // 市名称
        cityCode: '', // 市编码
        county: '', // 县名称
        countyCode: '', // 县编码
        detailedAddress: '',

        // 企业联系人
        declarant: '',
        declarantPhone: '',
        email: '',
        contactAddress: '',

        // 营业执照
        businessLicense: ''
      },
      rules: {
        unitName: [
          { required: true, message: '请输入单位名称' }
        ],
        creditCode: [
          {
            required: true,
            validator: (rule, value, callback) => {
              const normalizedValue = (value || '')
                .replace(/[\u200B-\u200D\uFEFF]/g, '')
                .replace(/\s+/g, '')
                .toUpperCase()
              if (!normalizedValue) {
                callback(new Error('请输入统一社会信用代码'))
                return
              }
              if (normalizedValue.length !== 18) {
                callback(new Error('统一社会信用代码应为18位'))
                return
              }
              if (!checkCreditCodeFormat(normalizedValue)) {
                callback(new Error('统一社会信用代码格式不正确'))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ],
        legalRepresentative: [
          { required: true, message: '请输入法定代表人' }
        ],
        legalPhone: [
          { required: true, message: '请输入法人电话' },
          { pattern: /^\d{11}$/, message: '法人电话必须为11位数字', trigger: 'blur' }
        ],
        legalIdNo: [
          { required: true, message: '请输入法人身份证号' },
          {
            pattern: /^\d{17}[\dXx]$/,
            message: '身份证号格式不正确，需为18位数字或末位X',
            trigger: 'blur'
          }
        ],
        establishmentDate: [
          { required: true, message: '请选择成立时间' }
        ],
        province: [
          { required: true, message: '请选择省份' }
        ],
        city: [
          { required: true, message: '请选择城市' }
        ],
        district: [
          { required: true, message: '请选择区县' }
        ],
        detailedAddress: [
          { required: true, message: '请输入详细地址' }
        ],
        declarant: [
          { required: true, message: '请输入信息申报责任人' }
        ],
        declarantPhone: [
          { required: true, message: '请输入申报人电话' },
          { pattern: /^\d{11}$/, message: '申报人电话必须为11位数字', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入电子邮箱' },
          { type: 'email', message: '请输入正确的邮箱格式' }
        ],
        contactAddress: [
          { required: true, message: '请输入联系地址' }
        ]
      }
    }
  },
  computed: {
    // 地区级联选择器配置
    regionCascaderProps() {
      return RegionUtils.getCascaderProps()
    },
    // 地区显示文本
    regionText() {
      const parts = []
      if (this.formData.province) parts.push(this.formData.province)
      if (this.formData.city) parts.push(this.formData.city)
      if (this.formData.county) parts.push(this.formData.county)
      return parts.join(' / ')
    },
    shouldShowAuditContactTip() {
      return this.msgCode == 200 && Number(this.dict.auditStatus) !== 1
    },
    auditStatusIcon() {
      const map = { 0: 'el-icon-time', 1: 'el-icon-circle-check', 2: 'el-icon-warning-outline' }
      return map[this.dict.auditStatus] || 'el-icon-info'
    }
  },
  mounted() {

    // 初始化表单地区字段
    RegionUtils.initRegionFields(this.formData)
    // 获取企业信息
    this.getOrganizationInfo().then(() => {
      // 企业信息不存在时，自动进入编辑模式
      if (this.msgCode !== 200) {
        this.isEditMode = true
        return
      }
      // 已有企业信息：根据审核状态决定是否自动进入编辑
      // 0 待审核、2 审核退回 -> 直接可编辑
      // 1 审核通过 -> 查看模式，需点击"编辑"按钮
      const auditStatus = Number(this.dict.auditStatus)
      if (auditStatus === 0 || auditStatus === 2) {
        this.handleEdit()
      }
    })
  },
  methods: {

    /**
      * 进入编辑模式
      */
    handleEdit() {
      this.isEditMode = true
      // 切换到编辑模式时，设置级联选择器的值
      this.$nextTick(() => {
        if (this.formData.provinceCode && this.formData.cityCode) {
          const regionArray = this.formData.countyCode
            ? [this.formData.provinceCode, this.formData.cityCode, this.formData.countyCode]
            : [this.formData.provinceCode, this.formData.cityCode]
          this.formData.region = regionArray
        }

        // 强制刷新级联选择器
        this.cascaderKey++
      })
    },

    /**
     * 取消编辑，重新获取数据
     */
    handleCancel() {
      this.isEditMode = false
      this.getOrganizationInfo()
    },

    async getOrganizationInfo() {
      try {
        const res = await getOrganizationInfo()
        this.msgCode = res.code
        if (res.code === 200 || res.code === 0) {
          this.dict = res.data

          const data = res.data
          if (data) {
            console.log('获取到的数据:', JSON.stringify(res.data));
            this.formData.id = data.id
            // 基本信息
            this.formData.unitName = data.unitName || ''
            this.formData.creditCode = (data.creditCode || '').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, '').toUpperCase()
            this.formData.legalRepresentative = data.legalRepresentative || ''
            this.formData.legalPhone = data.legalPhone || ''
            this.formData.legalIdNo = data.legalIdNo || ''
            this.formData.establishmentDate = data.establishmentDate || ''

            // 地区信息
            this.formData.province = data.province || ''
            this.formData.provinceCode = data.provinceCode || ''
            this.formData.city = data.city || ''
            this.formData.cityCode = data.cityCode || ''
            this.formData.county = data.county || ''
            this.formData.countyCode = data.countyCode || ''
            this.formData.detailedAddress = data.detailedAddress || ''

            // 企业联系人
            this.formData.declarant = data.declarant || ''
            this.formData.declarantPhone = data.declarantPhone || ''
            this.formData.email = data.email || ''
            this.formData.contactAddress = data.contactAddress || ''

            // 文件信息
            if (data.fileGuid) {
              this.fileGuid = data.fileGuid
              this.getAttachmentList(data.fileGuid)
            } else {
              this.fileGuid = this.fileGuid || new Date().getTime() + Math.random().toString().substr(2, 5)
            }
            
          
          }
        } else {
          this.fileGuid = new Date().getTime() + Math.random().toString().substr(2, 5)
        }
      } catch (error) {
        console.error('获取企业信息失败:', error)
      }
    },

    // 获取附件列表
    async getAttachmentList(fileGuid) {
      if (!fileGuid) {
        return
      }
      try {
        const res = await getFileList(fileGuid)
        if (res.code === 200 && Array.isArray(res.data) && res.data.length > 0) {
          this.formData.businessLicense = res.data[0].fileUrl
        } else {
          this.formData.businessLicense = ''
        }
      } catch (error) {
        console.error('获取附件列表失败:', error)
      } finally {
      }
    },
    async handleFileChange(file, fileList) {
      // 文件大小验证（10MB）
      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过10MB')
        return
      }
      // 文件格式验证
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.raw.type)) {
        this.$message.error('只支持 jpg、jpeg、png 格式的图片')
        return
      }

      // 创建图片预览URL
      const reader = new FileReader()
      reader.onload = (e) => {
        this.formData.businessLicense = e.target.result
      }
      reader.readAsDataURL(file.raw)

      // 上传文件到服务器
      if (!this.fileGuid) {
        this.fileGuid = new Date().getTime() + Math.random().toString().substr(2, 5)
      }
      this.uploading = true
      try {
        await doFileUpload(file.raw, {
          fileBindId: this.fileGuid,
          onSuccess: (response) => {
            console.log('文件上传成功:', response)
            this.uploadedFileId = response.fileId || response.id
            this.$message.success('文件上传成功')
            this.uploading = false
          },
          onError: (error) => {
            console.error('文件上传失败:', error)
            this.$message.error('文件上传失败，请重试')
            this.uploading = false
            // 清除预览
            this.formData.businessLicense = ''
          },
          onProgress: (percent) => {
            console.log('上传进度:', percent)
          }
        })
      } catch (error) {
        console.error('文件上传异常:', error)
        this.$message.error('文件上传失败')
        this.uploading = false
        this.formData.businessLicense = ''
      }
    },

    removeBusinessLicense() {
      // 删除营业执照
      this.formData.businessLicense = ''
      this.uploadedFileId = ''
      this.fileGuid = new Date().getTime() + Math.random().toString().substr(2, 5)
    },

    handlePreviewBusinessLicense() {
      if (!this.formData.businessLicense) {
        return
      }
      this.businessLicensePreviewVisible = true
    },

    /**
     * 地区选择变化处理
     */
    async handleRegionChange(value) {
      await RegionUtils.handleRegionChange(
        value,
        this.$refs.regionCascader,
        this.formData,
        { enableDebug: false },
        this,
        'REGION_CODE'
      )
    },

    async handleSave() {
      this.formData.creditCode = (this.formData.creditCode || '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\s+/g, '')
        .toUpperCase()
      // 验证所有表单
      const validatePromises = [
        this.$refs.organizationForm?.validate(),
        this.$refs.contactForm?.validate()
      ].filter(Boolean)

      try {
        await Promise.all(validatePromises)
      } catch (error) {
        this.$message.warning('请完善必填信息')
        return
      }

      // 验证营业执照是否上传
      if (!this.formData.businessLicense) {
        this.$message.warning('请上传三证合一扫描件')
        return
      }
      if (!this.fileGuid) {
        this.fileGuid = new Date().getTime() + Math.random().toString().substr(2, 5)
      }


      // 准备提交数据
      const submitData = {
        // 基本信息
        unitName: this.formData.unitName,
        creditCode: this.formData.creditCode,
        legalRepresentative: this.formData.legalRepresentative,
        legalPhone: this.formData.legalPhone,
        legalIdNo: this.formData.legalIdNo,
        establishmentDate: this.formData.establishmentDate,

        // 地区信息
        province: this.formData.province,
        provinceCode: this.formData.provinceCode,
        city: this.formData.city,
        cityCode: this.formData.cityCode,
        county: this.formData.county,
        countyCode: this.formData.countyCode,
        detailedAddress: this.formData.detailedAddress,

        // 企业联系人
        declarant: this.formData.declarant,
        declarantPhone: this.formData.declarantPhone,
        email: this.formData.email,
        contactAddress: this.formData.contactAddress,

        // 文件GUID - 用于关联上传的文件
        fileGuid: this.fileGuid
      }
      if (this.msgCode == 200) {
        submitData.id = this.formData.id
      }

      try {
        const res = await (this.msgCode == 200 ? updateOrganization(submitData) : addOrganization(submitData))
        if (res.code === 200 || res.code === 0) {
          this.$message.success(this.msgCode === 200 ? '修改成功' : '保存成功，请等待管理员审核')
          // 切换回查看模式
          this.isEditMode = false
          // 重新获取数据
          setTimeout(() => {
            this.getOrganizationInfo()
          }, 500)
        } else {
          this.$message.error(res.msg || res.message || '保存失败')
        }
      } catch (error) {
        console.error('保存企业信息失败:', error)
        this.$message.error('保存失败，请稍后重试')
      }
    },


    // auditStatus：0 待审核 1 审核通过 2 审核退回

    // 获取状态标签文字
    getStatusText(data) {
      const auditStatus = data.auditStatus
      const statusMap = {
        0: '待审核',
        1: '审核通过',
        2: '审核退回' + '退回原因' + (data.auditRemark ? '：' + data.auditRemark : '')
      }
      return statusMap[auditStatus] || '未提交'
    },

  }
}
</script>

<style lang="less" scoped>
@import '@/styles/minimal-gray-theme.less';

// ─── 页面容器 ─────────────────────────────────────────
.organization-modify-page {
  .apply-minimal-gray-theme();
  min-height: calc(100vh - 60px);
  padding-bottom: 80px;
}

// ─── 横幅页头 ─────────────────────────────────────────
.page-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  margin-bottom: 14px;
  background: linear-gradient(135deg, #f0f5ff 0%, #e8f4fd 100%);
  border: 1px solid #d6e8ff;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(24, 100, 220, 0.06);
}

.page-banner-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, @primary-color, lighten(@primary-color, 15%));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px fade(@primary-color, 30%);
  i { font-size: 26px; color: #fff; }
}

.page-banner-text { flex: 1; min-width: 0; }

.page-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: @gray-800;
  line-height: 1.3;
}

.page-desc {
  margin: 0;
  font-size: 13px;
  color: @gray-500;
}

.page-banner-status { flex-shrink: 0; }

// ─── 状态徽标 ─────────────────────────────────────────
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  i { font-size: 14px; }

  &.status-0  { background: #fff7e6; color: #d46b08; border: 1px solid #ffd591; }
  &.status-1  { background: #f6ffed; color: #389e0d; border: 1px solid #b7eb8f; }
  &.status-2  { background: #fff1f0; color: #cf1322; border: 1px solid #ffa39e; }
}

// ─── 审核提示条 ───────────────────────────────────────
.audit-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  margin-bottom: 14px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 10px;
  color: #d46b08;
  font-size: 13px;
  line-height: 1.6;
  i { font-size: 15px; }
  strong { font-weight: 700; }
}

// ─── 主体布局 ─────────────────────────────────────────
.info-layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1.5fr);
  align-items: start;
  gap: 14px;
}

.info-left {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.info-right {
  position: sticky;
  top: 16px;
}

// ─── 信息卡片 ─────────────────────────────────────────
.info-card {
  background: #fff;
  border: 1px solid @gray-200;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(16, 24, 40, 0.04);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  border-bottom: 1px solid @gray-100;
  background: @gray-50;
}

.card-accent {
  width: 4px;
  height: 18px;
  border-radius: 2px;
  background: linear-gradient(180deg, @primary-color, lighten(@primary-color, 20%));
  flex-shrink: 0;
}

.card-icon {
  font-size: 16px;
  color: @primary-color;
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: @gray-700;
}

.card-subtitle {
  font-size: 12px;
  color: @gray-400;
  margin-left: 4px;
}

.card-body {
  padding: 18px 18px 8px;
}

// ─── 表单控件 ─────────────────────────────────────────
::v-deep .el-form-item {
  margin-bottom: 16px;
}

::v-deep .el-form-item__label {
  color: @gray-600;
  font-size: 13px;
  font-weight: 500;
  line-height: 36px;
}

::v-deep .el-input__inner {
  height: 36px;
  border-radius: 8px;
  border: 1px solid @gray-300;
  color: @gray-700;
  transition: border-color .2s, box-shadow .2s;
}

::v-deep .el-input__inner:hover {
  border-color: lighten(@primary-color, 20%);
}

::v-deep .el-input__inner:focus {
  border-color: @primary-color;
  box-shadow: 0 0 0 3px fade(@primary-color, 10%);
}

::v-deep .el-input__inner[disabled],
::v-deep .el-input__inner[readonly] {
  background: @gray-50;
  color: @gray-600;
  cursor: default;
}

// ─── 单位所在地行 ─────────────────────────────────────
.location-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.region-cascader {
  width: 200px !important;
  flex-shrink: 0;
}

.location-input {
  flex: 1 !important;
}

// ─── 营业执照 ─────────────────────────────────────────
.license-preview-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 10px;
}

.license-img-wrap {
  position: relative;
  display: inline-block;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid @gray-200;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .08);
  img {
    display: block;
    width: 320px;
    height: 200px;
    object-fit: cover;
  }
}

.license-img-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 10px;
  padding-bottom: 12px;
  background: linear-gradient(to top, rgba(0, 0, 0, .65) 0%, transparent 60%);
  opacity: 0;
  transition: opacity .2s;
  ::v-deep .el-button { color: #fff; }
}

.license-img-wrap:hover .license-img-overlay {
  opacity: 1;
}

.license-uploaded-tip {
  font-size: 13px;
  color: #52c41a;
  display: flex;
  align-items: center;
  gap: 4px;
  i { font-size: 15px; }
}

.license-uploader {
  width: 100%;
  ::v-deep .el-upload { width: 100%; }
  ::v-deep .el-upload-dragger {
    width: 100%;
    height: 160px;
    border: 2px dashed @gray-300;
    border-radius: 12px;
    background: @gray-50;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: border-color .2s, background .2s;
    &:hover {
      border-color: @primary-color;
      background: fade(@primary-color, 4%);
    }
  }
}

.license-upload-inner {
  text-align: center;
  pointer-events: none;
}

.license-upload-icon {
  font-size: 38px;
  color: @gray-400;
  margin-bottom: 8px;
  display: block;
}

.license-upload-text {
  margin: 0 0 4px;
  font-size: 14px;
  color: @gray-600;
  font-weight: 500;
}

.license-upload-hint {
  margin: 0;
  font-size: 12px;
  color: @gray-400;
}

.license-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: @gray-400;
  i { font-size: 40px; margin-bottom: 8px; }
  p { margin: 0; font-size: 13px; }
}

// ─── 底部操作栏 ───────────────────────────────────────
.footer-bar {
  position: fixed;
  bottom: 0;
  left: 200px;
  right: 0;
  z-index: 1000;
  background: fade(#fff, 96%);
  backdrop-filter: blur(10px);
  border-top: 1px solid @gray-200;
  box-shadow: 0 -4px 20px rgba(16, 24, 40, 0.08);
}

.footer-bar-inner {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 14px;
  max-width: 1200px;
  margin: 0 auto;
}

.footer-bar-inner .el-button {
  min-width: 120px;
  height: 38px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
}

.footer-bar-inner .el-button--primary {
  box-shadow: 0 4px 14px fade(@primary-color, 35%);
}

// ─── 响应式 ───────────────────────────────────────────
@media (max-width: 900px) {
  .info-layout { grid-template-columns: 1fr; }
  .info-right { position: static; }
  .license-img-wrap img { width: 100%; height: auto; }
}

@media (max-width: 768px) {
  .organization-modify-page { padding-bottom: 88px; }
  .page-banner { flex-wrap: wrap; padding: 16px; }
  .page-title { font-size: 17px; }
  .location-row { flex-wrap: wrap; }
  .region-cascader, .location-input { width: 100% !important; }
  .footer-bar { left: 0; }
  .footer-bar-inner .el-button { flex: 1; min-width: 0; }
}
</style>
